// Reusable Modal Components for Admin Subscription Management

import React from 'react';
import {
    formatCurrency,
    formatDate,
    type SubscriptionPlan,
    type SubscriptionPreview,
    type SubscriptionHistory,
} from '../services/subscriptionAdminService';

/* ========================================
   CHANGE PLAN MODAL
   ======================================== */
interface ChangePlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    plans: SubscriptionPlan[];
    selectedPlanId: string;
    onSelectPlan: (planId: string) => void;
    adminNotes: string;
    onNotesChange: (notes: string) => void;
    preview: SubscriptionPreview | null;
    onPreview: (planId: string) => void;
    onConfirm: () => void;
    loading: boolean;
}

export const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
    isOpen,
    onClose,
    plans,
    selectedPlanId,
    onSelectPlan,
    adminNotes,
    onNotesChange,
    preview,
    onPreview,
    onConfirm,
    loading,
}) => {
    if (!isOpen) return null;

    const selectedPlan = plans.find(p => p.stripePriceId === selectedPlanId);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Change Subscription Plan</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Plan Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select New Plan *
                        </label>
                        <select
                            value={selectedPlanId}
                            onChange={(e) => {
                                onSelectPlan(e.target.value);
                                if (e.target.value) {
                                    onPreview(e.target.value);
                                }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        >
                            <option value="">-- Select a plan --</option>
                            {plans.map((plan) => (
                                <option key={plan.stripePriceId} value={plan.stripePriceId}>
                                    {plan.productName} - {formatCurrency(plan.amount, plan.currency)}/{plan.interval}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Preview Section */}
                    {preview && selectedPlan && (
                        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h3 className="font-semibold text-blue-900 mb-3">Change Preview</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-600 mb-1">Current Plan</p>
                                    <p className="font-medium text-gray-900">{formatCurrency(preview.currentPlan.amount)}/{preview.currentPlan.interval}</p>
                                </div>
                                <div>
                                    <p className="text-gray-600 mb-1">New Plan</p>
                                    <p className="font-medium text-gray-900">{formatCurrency(preview.newPlan.amount)}/{preview.newPlan.interval}</p>
                                </div>
                                <div>
                                    <p className="text-gray-600 mb-1">Price Difference</p>
                                    <p className={`font-semibold ${preview.priceDifference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {preview.priceDifference > 0 ? '+' : ''}{formatCurrency(Math.abs(preview.priceDifference))}
                                        <span className="text-gray-600 text-xs ml-1">
                                            ({preview.percentageChange > 0 ? '+' : ''}{preview.percentageChange.toFixed(1)}%)
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-600 mb-1">Effective Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(preview.effectiveDate)}</p>
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-blue-200">
                                <p className="text-sm text-blue-900">
                                    ⚠️ <strong>No immediate charge:</strong> Change takes effect on next billing cycle
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Admin Notes */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Admin Notes * (Reason for change)
                        </label>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => onNotesChange(e.target.value)}
                            placeholder="e.g., Customer upgrade request, billing dispute resolution..."
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        />
                    </div>

                    {/* Confirmation */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-yellow-900">
                            <strong>Important:</strong> The subscription will be modified using <code>proration_behavior: none</code> to prevent immediate charges. The user will be billed at the new rate on their next billing cycle.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading || !selectedPlanId || !adminNotes.trim()}
                            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Applying...' : 'Apply Change'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================
   BILLING EXEMPT MODAL
   ======================================== */
interface BillingExemptModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCurrentlyExempt: boolean;
    plans?: SubscriptionPlan[];
    selectedPlanId?: string;
    onSelectPlan?: (planId: string) => void;
    adminNotes: string;
    onNotesChange: (notes: string) => void;
    onConfirm: () => void;
    loading: boolean;
}

export const BillingExemptModal: React.FC<BillingExemptModalProps> = ({
    isOpen,
    onClose,
    isCurrentlyExempt,
    plans = [],
    selectedPlanId = '',
    onSelectPlan,
    adminNotes,
    onNotesChange,
    onConfirm,
    loading,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {isCurrentlyExempt ? 'Revoke Billing Exemption' : 'Grant Billing Exemption'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {isCurrentlyExempt ? (
                        <>
                            {/* Revoke Exemption */}
                            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <p className="text-sm text-orange-900">
                                    <strong>⚠️ Warning:</strong> Revoking billing exemption will create a new paid subscription and charge the user immediately.
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Plan * (User will be charged immediately)
                                </label>
                                <select
                                    value={selectedPlanId}
                                    onChange={(e) => onSelectPlan?.(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={loading}
                                >
                                    <option value="">-- Select a plan --</option>
                                    {plans.map((plan) => (
                                        <option key={plan.stripePriceId} value={plan.stripePriceId}>
                                            {plan.productName} - {formatCurrency(plan.amount, plan.currency)}/{plan.interval}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Grant Exemption */}
                            <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <p className="text-sm text-purple-900">
                                    <strong>ℹ️ Info:</strong> Granting billing exemption will:
                                </p>
                                <ul className="list-disc list-inside mt-2 text-sm text-purple-900 space-y-1">
                                    <li>Cancel existing Stripe subscription at period end</li>
                                    <li>Grant lifetime free access to all features</li>
                                    <li>User will not be charged going forward</li>
                                    <li>Exemption can be revoked later</li>
                                </ul>
                            </div>
                        </>
                    )}

                    {/* Admin Notes */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for {isCurrentlyExempt ? 'Revocation' : 'Exemption'} * (Required)
                        </label>
                        <textarea
                            value={adminNotes}
                            onChange={(e) => onNotesChange(e.target.value)}
                            placeholder={isCurrentlyExempt
                                ? "e.g., VIP period expired, promotional period ended..."
                                : "e.g., VIP customer - lifetime free, Staff account, Promotional access..."
                            }
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={
                                loading ||
                                !adminNotes.trim() ||
                                (isCurrentlyExempt && !selectedPlanId)
                            }
                            className={`flex-1 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${isCurrentlyExempt
                                ? 'bg-orange-600 hover:bg-orange-700'
                                : 'bg-purple-600 hover:bg-purple-700'
                                }`}
                        >
                            {loading ? 'Processing...' : `Confirm ${isCurrentlyExempt ? 'Revocation' : 'Exemption'}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================
   CANCEL/REACTIVATE MODAL
   ======================================== */
interface CancelModalProps {
    isOpen: boolean;
    onClose: () => void;
    immediate: boolean;
    onImmediateChange: (immediate: boolean) => void;
    reason: string;
    onReasonChange: (reason: string) => void;
    onConfirm: () => void;
    loading: boolean;
    currentPeriodEnd?: string;
}

export const CancelModal: React.FC<CancelModalProps> = ({
    isOpen,
    onClose,
    immediate,
    onImmediateChange,
    reason,
    onReasonChange,
    onConfirm,
    loading,
    currentPeriodEnd,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Cancel Subscription</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Cancellation Options */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Cancellation Type *
                        </label>
                        <div className="space-y-3">
                            <label className="flex items-start p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                <input
                                    type="radio"
                                    checked={!immediate}
                                    onChange={() => onImmediateChange(false)}
                                    className="mt-1 mr-3"
                                    disabled={loading}
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Cancel at Period End (Recommended)</div>
                                    <div className="text-sm text-gray-600 mt-1">
                                        User retains access until {currentPeriodEnd ? formatDate(currentPeriodEnd) : 'end of billing period'}
                                    </div>
                                </div>
                            </label>

                            <label className="flex items-start p-4 border border-red-300 rounded-lg cursor-pointer hover:bg-red-50">
                                <input
                                    type="radio"
                                    checked={immediate}
                                    onChange={() => onImmediateChange(true)}
                                    className="mt-1 mr-3"
                                    disabled={loading}
                                />
                                <div>
                                    <div className="font-medium text-red-900">Cancel Immediately</div>
                                    <div className="text-sm text-red-700 mt-1">
                                        ⚠️ User loses access immediately. Use only for violations or fraud.
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Reason */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Cancellation Reason
                        </label>
                        <select
                            value={reason}
                            onChange={(e) => onReasonChange(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={loading}
                        >
                            <option value="">-- Select reason --</option>
                            <option value="Customer Request">Customer Request</option>
                            <option value="Payment Fraud">Payment Fraud</option>
                            <option value="Terms Violation">Terms Violation</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    {/* Warning */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <p className="text-sm text-yellow-900">
                            <strong>⚠️ Important:</strong> This action will update the subscription in Stripe and Firestore. An audit log entry will be created.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading}
                            className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Canceling...' : 'Confirm Cancellation'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface ReactivateModalProps {
    isOpen: boolean;
    onClose: () => void;
    plans: SubscriptionPlan[];
    selectedPlanId: string;
    onSelectPlan: (planId: string) => void;
    onConfirm: () => void;
    loading: boolean;
    needsNewPlan: boolean;
}

export const ReactivateModal: React.FC<ReactivateModalProps> = ({
    isOpen,
    onClose,
    plans,
    selectedPlanId,
    onSelectPlan,
    onConfirm,
    loading,
    needsNewPlan,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Reactivate Subscription</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={loading}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {needsNewPlan && (
                        <>
                            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-900">
                                    The subscription was fully canceled. You need to select a plan to create a new subscription.
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Plan *
                                </label>
                                <select
                                    value={selectedPlanId}
                                    onChange={(e) => onSelectPlan(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={loading}
                                >
                                    <option value="">-- Select a plan --</option>
                                    {plans.map((plan) => (
                                        <option key={plan.stripePriceId} value={plan.stripePriceId}>
                                            {plan.productName} - {formatCurrency(plan.amount, plan.currency)}/{plan.interval}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {!needsNewPlan && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                            <p className="text-sm text-green-900">
                                The subscription will resume with the existing plan and settings. The user will be charged on the next billing cycle.
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={loading || (needsNewPlan && !selectedPlanId)}
                            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Reactivating...' : 'Confirm Reactivation'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================
   SUBSCRIPTION HISTORY MODAL
   ======================================== */
interface HistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    history: SubscriptionHistory | null;
    loading: boolean;
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
    isOpen,
    onClose,
    history,
    loading,
}) => {
    if (!isOpen) return null;

    const exportToCSV = () => {
        if (!history) return;

        const headers = ['Date', 'Action', 'Admin', 'Old Plan', 'New Plan', 'Notes', 'Status'];
        const rows = history.history.map(entry => [
            formatDate(entry.timestamp),
            entry.action,
            entry.adminEmail,
            entry.metadata.oldPlanId || '-',
            entry.metadata.newPlanId || '-',
            entry.metadata.adminNotes || entry.metadata.reason || '-',
            entry.success ? 'Success' : 'Failed',
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `subscription-history-${history.userId}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-gray-900">Subscription History</h2>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={exportToCSV}
                                disabled={loading || !history?.history.length}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                            >
                                Export CSV
                            </button>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-8 text-gray-600">Loading history...</div>
                    ) : history && history.history.length > 0 ? (
                        <div className="space-y-4">
                            {history.history.map((entry) => (
                                <div
                                    key={entry.logId}
                                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${entry.success
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                {entry.action.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <span className="text-sm text-gray-600">{formatDate(entry.timestamp)}</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                                        <div>
                                            <span className="text-gray-600">Admin:</span>
                                            <span className="ml-2 font-medium">{entry.adminEmail}</span>
                                        </div>
                                        {entry.metadata.oldPlanId && (
                                            <div>
                                                <span className="text-gray-600">Old Plan:</span>
                                                <span className="ml-2 font-mono text-xs">{entry.metadata.oldPlanId}</span>
                                            </div>
                                        )}
                                        {entry.metadata.newPlanId && (
                                            <div>
                                                <span className="text-gray-600">New Plan:</span>
                                                <span className="ml-2 font-mono text-xs">{entry.metadata.newPlanId}</span>
                                            </div>
                                        )}
                                    </div>

                                    {(entry.metadata.adminNotes || entry.metadata.reason) && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <span className="text-sm text-gray-600">Notes: </span>
                                            <span className="text-sm text-gray-900">
                                                {entry.metadata.adminNotes || entry.metadata.reason}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-600">
                            No subscription history found
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
